import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useHR } from "./HRContext";
import { useAuth } from "../context/AuthContext";
import {
  HRDashboardStats,
  HREmployee,
  HRDepartment,
  HRContract,
} from "../types/hr";
import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Progress,
  List,
  Tag,
  Button,
  Divider,
  Spin,
  Alert,
  Tooltip,
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  DollarOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

const { Content } = Layout;
const { Title, Text } = Typography;

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { employees, departments, positions, contracts, loading, errors } =
    useHR();

  const [stats, setStats] = useState<HRDashboardStats>({
    totalEmployees: 0,
    activeEmployees: 0,
    departments: 0,
    positions: 0,
    contracts: {
      total: 0,
      expiring: 0,
      cdi: 0,
      cdd: 0,
    },
    payroll: {
      totalGross: 0,
      totalNet: 0,
      averageSalary: 0,
    },
  });

  const [expiringContracts, setExpiringContracts] = useState<any[]>([]);

  // Calculer les statistiques avec useMemo pour optimiser les performances
  const { calculatedStats, expiringContractsList } = useMemo(() => {
    const activeEmployees = employees.filter(
      (emp: HREmployee) => emp.status === "active"
    );
    const activeContracts = contracts.filter(
      (contract: HRContract) => contract.status === "active"
    );

    // Calculer les contrats arrivant à échéance (30 jours)
    const now = new Date();
    const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

    const expiring = activeContracts.filter((contract: HRContract) => {
      if (!contract.end_date) return false;
      const endDate = new Date(contract.end_date);
      return endDate >= now && endDate <= thirtyDaysLater;
    });

    // Calculer les totaux de paie (basé sur les contrats actifs)
    const totalGross = activeContracts.reduce((sum: number, contract: HRContract) => {
      const grossSalary = Number(contract.gross_salary) || 0;
      return sum + grossSalary;
    }, 0);
    
    const averageSalary = activeContracts.length > 0 
      ? totalGross / activeContracts.length 
      : 0;

    const calculatedStats = {
      totalEmployees: employees.length,
      activeEmployees: activeEmployees.length,
      departments: departments.length,
      positions: positions.length,
      contracts: {
        total: contracts.length,
        expiring: expiring.length,
        cdi: activeContracts.filter(
          (c: HRContract) =>
            c.contract_type_name?.toLowerCase().includes("cdi") ||
            c.contract_type_name?.toLowerCase().includes("durée indéterminée")
        ).length,
        cdd: activeContracts.filter(
          (c: HRContract) =>
            c.contract_type_name?.toLowerCase().includes("cdd") ||
            c.contract_type_name?.toLowerCase().includes("durée déterminée")
        ).length,
      },
      payroll: {
        totalGross: totalGross,
        totalNet: totalGross * 0.75, // Estimation approximative
        averageSalary: averageSalary,
      },
    };

    return { calculatedStats, expiringContractsList: expiring };
  }, [employees, departments, positions, contracts]);

  // Utiliser les valeurs calculées pour mettre à jour les états
  useEffect(() => {
    setStats(calculatedStats);
    setExpiringContracts(expiringContractsList);
  }, [calculatedStats, expiringContractsList]);

  const formatCurrency = useCallback((amount: number) => {
    const validAmount = isNaN(amount) || !isFinite(amount) ? 0 : amount;
    return new Intl.NumberFormat("fr-BF", {
      style: "currency",
      currency: "XOF",
      minimumFractionDigits: 0,
    }).format(validAmount);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR");
  }, []);

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  const departmentStats = useMemo(() => {
    return departments.slice(0, 5).map((dept: HRDepartment) => {
      const employeeCount = employees.filter(
        (emp: HREmployee) => emp.department_id === dept.id
      ).length;
      const percentage =
        stats.totalEmployees > 0
          ? Math.round((employeeCount / stats.totalEmployees) * 100)
          : 0;
      return { dept, employeeCount, percentage };
    });
  }, [departments, employees, stats.totalEmployees]);

  const recentHires = useMemo(() => {
    return employees
      .filter((emp: HREmployee) => emp.status === "active")
      .sort(
        (a: HREmployee, b: HREmployee) =>
          new Date(b.hire_date).getTime() - new Date(a.hire_date).getTime()
      )
      .slice(0, 5);
  }, [employees]);



  if (
    loading.employees ||
    loading.departments ||
    loading.positions ||
    loading.contracts
  ) {
    return (
      <Layout>
        <Content style={{ padding: "24px" }}>
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: "24px" }}>
        {/* Header */}
        <Card className="mb-6" bordered={false}>
          <Title level={2} className="!mb-2">
            Tableau de bord RH
          </Title>
          <Text type="secondary">
            Vue d'ensemble des ressources humaines de votre entreprise
          </Text>
        </Card>

        {/* Erreurs */}
        {Object.values(errors).some((error) => error) && (
          <Alert
            type="error"
            showIcon
            className="mb-6"
            message="Erreurs de chargement"
            description={
              <ul className="list-disc list-inside">
                {Object.entries(errors).map(([key, error]) =>
                  error ? (
                    <li key={key}>
                      {key}: {error}
                    </li>
                  ) : null
                )}
              </ul>
            }
          />
        )}

        {/* KPI Cards */}
        <Row gutter={[16, 16]} className="mb-8">
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable>
              <Statistic
                title="Employés Actifs"
                value={stats.activeEmployees}
                prefix={<UserOutlined style={{ color: "#1890ff" }} />}
                suffix={`/ ${stats.totalEmployees}`}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card hoverable>
              <Statistic
                title="Départements"
                value={stats.departments}
                prefix={<TeamOutlined style={{ color: "#52c41a" }} />}
                suffix={`postes: ${stats.positions}`}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card hoverable>
              <Statistic
                title="Contrats"
                value={stats.contracts.total}
                prefix={<FileTextOutlined style={{ color: "#722ed1" }} />}
                suffix={
                  <Tooltip
                    title={`CDI: ${stats.contracts.cdi}, CDD: ${stats.contracts.cdd}`}
                  >
                    <span style={{ cursor: "help" }}>types: 2</span>
                  </Tooltip>
                }
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card hoverable>
              <Statistic
                title="Masse Salariale"
                value={stats.payroll.totalGross}
                prefix={<DollarOutlined style={{ color: "#fa8c16" }} />}
                formatter={(value: any) => formatCurrency(value)}
                valueStyle={{ color: "#fa8c16", fontSize: "18px" }}
              />
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Moy: {formatCurrency(stats.payroll.averageSalary)}
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Navigation rapide */}
        <Card title="Navigation rapide" className="mb-8">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                className="h-full"
                bodyStyle={{ padding: "16px" }}
                onClick={() => handleNavigate('/rh/employes')}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <UserOutlined className="text-blue-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <Title level={5} className="!mb-1">
                      Employés
                    </Title>
                    <Text type="secondary">Gérer le personnel</Text>
                  </div>
                  <ArrowRightOutlined className="text-gray-400" />
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                className="h-full"
                bodyStyle={{ padding: "16px" }}
                onClick={() => handleNavigate('/rh/departements')}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <TeamOutlined className="text-green-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <Title level={5} className="!mb-1">
                      Départements
                    </Title>
                    <Text type="secondary">Structure organisationnelle</Text>
                  </div>
                  <ArrowRightOutlined className="text-gray-400" />
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                className="h-full"
                bodyStyle={{ padding: "16px" }}
                onClick={() => handleNavigate('/rh/contrats')}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <FileTextOutlined className="text-purple-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <Title level={5} className="!mb-1">
                      Contrats
                    </Title>
                    <Text type="secondary">Gestion contractuelle</Text>
                  </div>
                  <ArrowRightOutlined className="text-gray-400" />
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                className="h-full"
                bodyStyle={{ padding: "16px" }}
                onClick={() => handleNavigate('/rh/traitement-paie')}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-orange-50 rounded-lg">
                    <DollarOutlined className="text-orange-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <Title level={5} className="!mb-1">
                      Traitement Paie
                    </Title>
                    <Text type="secondary">Calculs et traitement des salaires</Text>
                  </div>
                  <ArrowRightOutlined className="text-gray-400" />
                </div>
              </Card>
            </Col>
            
            <Col xs={24} sm={12} md={8} lg={6}>
              <Card
                hoverable
                className="h-full"
                bodyStyle={{ padding: "16px" }}
                onClick={() => handleNavigate('/rh/bulletins-paie')}
              >
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-cyan-50 rounded-lg">
                    <FileTextOutlined className="text-cyan-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <Title level={5} className="!mb-1">
                      Bulletins de Paie
                    </Title>
                    <Text type="secondary">Consulter et gérer les bulletins</Text>
                  </div>
                  <ArrowRightOutlined className="text-gray-400" />
                </div>
              </Card>
            </Col>
          </Row>
        </Card>

        {/* Alertes - Contrats arrivant à échéance */}
        {stats.contracts.expiring > 0 && (
          <Alert
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            className="mb-8"
            message={`⚠️ Contrats arrivant à échéance (${stats.contracts.expiring})`}
            description={
              <div className="mt-3">
                <List
                  size="small"
                  dataSource={expiringContracts.slice(0, 5)}
                  renderItem={(contract) => (
                    <List.Item className="flex justify-between items-center">
                      <Text>
                        {contract.first_name} {contract.last_name}
                      </Text>
                      <Tag color="orange">{formatDate(contract.end_date)}</Tag>
                    </List.Item>
                  )}
                />
                {expiringContracts.length > 5 && (
                  <Text type="secondary" className="text-xs">
                    ... et {expiringContracts.length - 5} autres contrats
                  </Text>
                )}
                <div className="mt-3">
                  <Button type="link" size="small">
                    Voir tous les contrats →
                  </Button>
                </div>
              </div>
            }
          />
        )}

        {/* Graphiques et tableaux récents */}
        <Row gutter={[16, 16]}>
          {/* Répartition par département */}
          <Col xs={24} lg={12}>
            <Card title="Répartition par département" className="h-full">
              <div className="space-y-4">
                {departmentStats.map(({ dept, employeeCount, percentage }) => (
                  <div key={dept.id}>
                    <div className="flex justify-between items-center mb-2">
                      <Text strong>{dept.name}</Text>
                      <Text type="secondary">{employeeCount} employés</Text>
                    </div>
                    <Progress
                      percent={percentage}
                      size="small"
                      strokeColor="#1890ff"
                    />
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          {/* Embauches récentes */}
          <Col xs={24} lg={12}>
            <Card title="Embauches récentes" className="h-full">
              <List
                dataSource={recentHires}
                renderItem={(employee: HREmployee) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Text className="text-blue-600 font-medium text-sm">
                            {employee.first_name?.[0]}
                            {employee.last_name?.[0]}
                          </Text>
                        </div>
                      }
                      title={
                        <Text strong>
                          {employee.first_name} {employee.last_name}
                        </Text>
                      }
                      description={
                        <Text type="secondary" className="text-xs">
                          {employee.position_title || "Poste non défini"} •{" "}
                          {formatDate(employee.hire_date)}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default Dashboard;
